import React, { useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../constants/firebaseModular';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import type {
  Chart,
  HipaaConsentState,
  RequestHipaaRevocationRequest,
  RequestHipaaRevocationResponse,
  RequestHipaaReauthRequest,
  RequestHipaaReauthResponse,
} from '@usacsmideo/types';
import { Button } from './buttons/Button';
import * as Sentry from '@sentry/browser';

interface EmergencyCardSectionProps {
  chart: Chart;
}

/**
 * Emergency card section on the patient profile page.
 * Reads consent state from chart.HIPAA_AUTH.hipaaConsentState (CU-86agmta3g).
 */
export function EmergencyCardSection({ chart }: EmergencyCardSectionProps) {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();
  const sigRef = useRef<SignatureCanvas | null>(null);

  const [activeCardCount, setActiveCardCount] = React.useState(0);
  const [cardExpiryDate, setCardExpiryDate] = React.useState<string | null>(null);
  const [cardStatusLoaded, setCardStatusLoaded] = React.useState(false);
  const [showSignature, setShowSignature] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const patientId = currentUser?.uid;

  // Read consent state directly from chart data — no ConsentHistory queries
  const consentStatus: HipaaConsentState = chart?.HIPAA_AUTH?.hipaaConsentState?.status ?? 'none';

  // Fetch active card status
  React.useEffect(() => {
    if (chart?.STATUS?.emergencyAccessTokenId) {
      const getStatus = httpsCallable<void, { activeCount: number; expiryDate: string | null }>(
        functions,
        'getEmergencyCardStatus',
      );
      getStatus()
        .then((result) => {
          setActiveCardCount(result.data.activeCount);
          setCardExpiryDate(result.data.expiryDate);
          setCardStatusLoaded(true);
        })
        .catch((error: unknown) => {
          Sentry.captureException(error);
          setCardStatusLoaded(true);
        });
    } else {
      setCardStatusLoaded(true);
    }
  }, [chart?.STATUS?.emergencyAccessTokenId]);

  const handleSubmitRevocation = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      alert('Please sign before submitting.');
      return;
    }
    if (!patientId) return;

    const signatureDataURI = sigRef.current.getTrimmedCanvas().toDataURL('image/png');

    setIsSubmitting(true);
    try {
      const requestRevocation = httpsCallable<
        RequestHipaaRevocationRequest,
        RequestHipaaRevocationResponse
      >(functions, 'requestHipaaRevocation');

      await requestRevocation({
        patientId,
        signatureDataURI,
        platform: 'web',
      });

      setShowSignature(false);
      queryClient.invalidateQueries({ queryKey: ['chart', patientId] });
    } catch (error: unknown) {
      Sentry.captureException(error);
      const message =
        error instanceof Error ? error.message : 'Failed to submit revocation request.';
      alert(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestReauth = async () => {
    if (!patientId) return;

    setIsSubmitting(true);
    try {
      const requestReauth = httpsCallable<RequestHipaaReauthRequest, RequestHipaaReauthResponse>(
        functions,
        'requestHipaaReauth',
      );

      await requestReauth({ patientId, platform: 'web' });
      queryClient.invalidateQueries({ queryKey: ['chart', patientId] });
    } catch (error: unknown) {
      Sentry.captureException(error);
      const message =
        error instanceof Error ? error.message : 'Failed to submit re-authorization request.';
      alert(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!cardStatusLoaded) return null;

  return (
    <div className="mt-8 w-5/6">
      <p className="text-mideo-darkred text-center text-4xl font-bold">My Emergency Card</p>

      {/* Card status display — only when consent is active */}
      {activeCardCount > 0 && consentStatus === 'granted' && (
        <div className="mt-4 flex flex-col items-center">
          <p className="text-sm text-gray-600">
            You have {activeCardCount} active emergency {activeCardCount === 1 ? 'card' : 'cards'}.
          </p>
          {cardExpiryDate && (
            <p className="mt-1 text-sm text-gray-600">Expires: {cardExpiryDate}</p>
          )}
        </div>
      )}

      {/* REVOKED STATE — show re-open button */}
      {consentStatus === 'revoked' && (
        <div className="mt-4 flex flex-col items-center">
          <div className="rounded-md bg-red-50 border border-red-200 p-4 max-w-md text-center">
            <p className="text-sm font-semibold text-red-800">HIPAA Authorization Revoked</p>
            <p className="mt-1 text-sm text-red-700">
              Your HIPAA authorization has been revoked. Your emergency access card is no longer
              active.
            </p>
            <button
              type="button"
              onClick={handleRequestReauth}
              disabled={isSubmitting}
              className="mt-3 inline-flex justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Re-Open Application'}
            </button>
          </div>
        </div>
      )}

      {/* RE-AUTH REQUESTED STATE */}
      {consentStatus === 'reauth_requested' && (
        <div className="mt-4 flex flex-col items-center">
          <div className="rounded-md bg-amber-50 border border-amber-300 p-4 max-w-md text-center">
            <p className="text-sm font-semibold text-amber-800">Re-Authorization Pending</p>
            <p className="mt-1 text-sm text-amber-700">
              Your request to re-open your application has been submitted. A MIDEO team member will
              review and process your request.
            </p>
          </div>
        </div>
      )}

      {/* REVOKE REQUESTED STATE */}
      {consentStatus === 'revoke_requested' && (
        <div className="mt-4 flex flex-col items-center">
          <div className="rounded-md bg-amber-50 border border-amber-300 p-4 max-w-md text-center">
            <p className="text-sm font-semibold text-amber-800">Revocation Request Pending</p>
            <p className="mt-1 text-sm text-amber-700">
              Your HIPAA revocation request has been submitted. A MIDEO team member will review and
              process your request.
            </p>
          </div>
        </div>
      )}

      {/* REVOCATION REQUEST FLOW — only show when consent is active (granted) */}
      {consentStatus === 'granted' && (
        <div className="mt-6 flex flex-col items-center">
          {!showSignature ? (
            <Button
              id="request-revocation-button"
              textContent="Submit HIPAA Revocation Request"
              type="button"
              onClick={() => setShowSignature(true)}
            />
          ) : (
            <div className="w-full max-w-md">
              {/* Explanation */}
              <div className="rounded-md bg-amber-50 border border-amber-300 p-4 mb-4">
                <p className="text-sm font-semibold text-amber-800">Before you sign</p>
                <p className="mt-1 text-sm text-amber-700">
                  This will submit a request to revoke your HIPAA authorization. Once approved by a
                  MIDEO team member, your emergency access card will stop working and your health
                  information will no longer be accessible via MIDEO.
                </p>
              </div>

              {/* Signature capture */}
              <p className="text-sm font-medium text-gray-700 mb-2">Sign below to confirm</p>
              <div className="border border-gray-300 rounded-md bg-white">
                <SignatureCanvas
                  ref={sigRef}
                  backgroundColor="#ffffff"
                  canvasProps={{
                    height: 250,
                    className: 'w-full',
                    id: 'revocation-sig-canvas',
                  }}
                />
              </div>

              <div className="mt-3 flex gap-3 justify-center">
                <button
                  type="button"
                  onClick={() => sigRef.current?.clear()}
                  className="inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowSignature(false);
                    sigRef.current?.clear();
                  }}
                  className="inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmitRevocation}
                  disabled={isSubmitting}
                  className="inline-flex justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Revocation Request'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
